import {
  ClientRect,
  DroppableContainer,
  KeyboardCode,
  KeyboardCoordinateGetter,
  closestCorners,
  getFirstCollision,
} from "@dnd-kit/core";

const DIRECTION_CODES: string[] = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

function isInDirection(code: KeyboardCode, rect: ClientRect, collisionRect: ClientRect): boolean {
  switch (code) {
    case KeyboardCode.Down:
      return rect.top > collisionRect.top;
    case KeyboardCode.Up:
      return rect.top < collisionRect.top;
    case KeyboardCode.Right:
      return rect.left > collisionRect.left;
    case KeyboardCode.Left:
      return rect.left < collisionRect.left;
    default:
      return false;
  }
}

/**
 * Keyboard coordinate getter that jumps the dragged document straight to the closest enabled drop
 * zone in the pressed arrow direction. The panel zones and the asset inventory upload areas are
 * far apart, so the default fixed-pixel arrow movement would be unusable; disabled zones (targets
 * the drag is not allowed to enter) are skipped automatically.
 */
export const documentDndKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  { context: { active, collisionRect, droppableRects, droppableContainers } }
) => {
  if (!DIRECTION_CODES.includes(event.code)) {
    return undefined;
  }

  event.preventDefault();

  if (!active || !collisionRect) {
    return undefined;
  }

  const direction = event.code as KeyboardCode;
  const candidates: DroppableContainer[] = droppableContainers.getEnabled().filter((container) => {
    const rect = droppableRects.get(container.id);
    return rect !== undefined && isInDirection(direction, rect, collisionRect);
  });

  const collisions = closestCorners({
    active,
    collisionRect,
    droppableRects,
    droppableContainers: candidates,
    pointerCoordinates: null,
  });

  const closestId = getFirstCollision(collisions, "id");
  if (closestId === null) {
    return undefined;
  }

  const newRect = droppableRects.get(closestId);
  return newRect ? { x: newRect.left, y: newRect.top } : undefined;
};
